import React from "react";
import { Typography } from "antd";
import type { Booking } from "../../types/booking";
import { getPrimaryCommercialInvoicePath } from "./commercialInvoiceAnalysisService";
import {
  bookingHasCurrentCiAnalysis,
  bookingNeedsAnyCiCheck,
} from "../../utils/agentBookingCiAnalysis";
import {
  getAnalysisResultFromBookingEmbed,
  getCommercialInvoiceValidityBreakdown,
  getValidityBreakdownFromBooking,
} from "./commercialInvoiceValidity";
import { CommercialInvoiceValidityDisplay } from "./CommercialInvoiceValidityDisplay";
import { CommercialInvoiceDualCheckProgress } from "./CommercialInvoiceDualCheckProgress";
import type { CiDualCheckRunState } from "./ciDualCheckRunState";
import { CI_DUAL_CHECK_IDLE, isCiDualCheckActive } from "./ciDualCheckRunState";

const { Text } = Typography;

export interface CommercialInvoiceAnalysisSummaryProps {
  booking: Booking;
  runState?: CiDualCheckRunState;
  onOpenDocuments?: () => void;
}

const DocumentsLink: React.FC<{ onOpenDocuments?: () => void }> = ({
  onOpenDocuments,
}) => {
  if (!onOpenDocuments) return null;
  return (
    <Typography.Link onClick={onOpenDocuments} style={{ fontSize: 11 }}>
      Documents
    </Typography.Link>
  );
};

/** Compact CI validity readout for booking overview tabs. */
export const CommercialInvoiceAnalysisSummary: React.FC<
  CommercialInvoiceAnalysisSummaryProps
> = ({ booking, runState = CI_DUAL_CHECK_IDLE, onOpenDocuments }) => {
  const primaryPath = getPrimaryCommercialInvoicePath(booking.commercial_invoice_file);
  const checksActive = isCiDualCheckActive(runState);
  const aiCheckPending =
    runState.deepCheck === "running" || runState.deepCheck === "pending";
  const liveResult = runState.liveResult ?? undefined;
  const embeddedResult = getAnalysisResultFromBookingEmbed(booking);
  const analysisResult = liveResult ?? embeddedResult;

  if (!primaryPath) {
    return (
      <div style={{ marginTop: 4, marginBottom: 8 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Commercial invoice: none uploaded ·{" "}
        </Text>
        <DocumentsLink onOpenDocuments={onOpenDocuments} />
      </div>
    );
  }

  const showAutomaticResults =
    Boolean(analysisResult) ||
    runState.heuristic === "done" ||
    bookingHasCurrentCiAnalysis(booking);

  const breakdown = analysisResult
    ? getCommercialInvoiceValidityBreakdown(analysisResult, booking)
    : showAutomaticResults
      ? getValidityBreakdownFromBooking(booking)
      : null;

  if (checksActive || bookingNeedsAnyCiCheck(booking)) {
    return (
      <div style={{ marginTop: 2, marginBottom: 4, maxWidth: 280 }}>
        <CommercialInvoiceDualCheckProgress runState={runState} />
        {showAutomaticResults && breakdown ? (
          <CommercialInvoiceValidityDisplay
            breakdown={breakdown}
            compact
            showAiNote
            aiCheckPending={aiCheckPending}
            extra={<DocumentsLink onOpenDocuments={onOpenDocuments} />}
          />
        ) : (
          <DocumentsLink onOpenDocuments={onOpenDocuments} />
        )}
      </div>
    );
  }

  if (!bookingHasCurrentCiAnalysis(booking)) {
    return (
      <div style={{ marginTop: 4, marginBottom: 8 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          CI analysis: not run yet ·{" "}
        </Text>
        <DocumentsLink onOpenDocuments={onOpenDocuments} />
      </div>
    );
  }

  if (!breakdown) return null;

  return (
    <div style={{ marginTop: 2, marginBottom: 4, maxWidth: 280 }}>
      <CommercialInvoiceValidityDisplay
        breakdown={breakdown}
        compact
        showAiNote
        extra={<DocumentsLink onOpenDocuments={onOpenDocuments} />}
      />
    </div>
  );
};
